import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Microphone } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/AuthContext";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Welcome back");
      nav(from, { replace: true });
    } else {
      setError(r.error);
    }
  }

  return (
    <div className="min-h-screen bg-[#C7C9CC] text-[#0A0A0A] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden bg-gradient-to-br from-[#BFC2C6] via-[#C7C9CC] to-[#B0B3B7]">
        <Link to="/" className="font-display text-2xl tracking-tighter" data-testid="brand-logo">BEAT BOX</Link>
        {/* Huge wordmark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="bb-massive text-white/40 text-[24vw]">SIGN<br />IN</span>
        </div>
        <div className="relative z-10">
          <span className="bb-tag">— BACK TO THE BOOTH</span>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 tracking-tighter leading-[0.9]">
            Your projects<br />are waiting.
          </h1>
          <p className="text-[#2B2B2B] mt-5 max-w-md">Pick up the take you left mid-loop, drag in a new layer, or render a mix you started last week.</p>
        </div>
        <p className="font-mono text-[#6E7378] text-xs uppercase tracking-widest relative z-10">CTRL ROOM · 001</p>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-[#E2E3E5]">
        <form onSubmit={handleSubmit} className="w-full max-w-md bb-card p-8 lg:p-10" data-testid="login-form">
          <Link to="/" className="font-display text-xl tracking-tighter lg:hidden block mb-6">BEAT BOX</Link>
          <span className="bb-tag" data-testid="login-overline">SIGN IN</span>
          <h2 className="font-display text-4xl lg:text-5xl tracking-tighter mt-4 mb-8 leading-[0.95]">
            Welcome back.
          </h2>
          <div className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-[#6E7378] uppercase text-xs tracking-widest font-mono">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-white border-[#D0D2D5] rounded-full h-12 px-5 focus-visible:ring-1 focus-visible:ring-[#A8FF1F]"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#6E7378] uppercase text-xs tracking-widest font-mono">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-white border-[#D0D2D5] rounded-full h-12 px-5 focus-visible:ring-1 focus-visible:ring-[#A8FF1F]"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="text-[#E11D48] text-sm font-mono" data-testid="login-error">{error}</div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="bb-pill w-full bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] h-12 font-semibold"
            >
              {loading ? "Authorizing…" : "Sign in"} <ArrowRight className="ml-2" size={16} weight="bold" />
            </Button>
            <GoogleSignInButton />
          </div>
          <p className="text-[#6E7378] text-sm mt-6 text-center">
            No account?{" "}
            <Link to="/register" data-testid="link-register" className="text-[#0A0A0A] font-semibold underline underline-offset-4 decoration-[#A8FF1F] decoration-[3px]">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
